import { DivisionDTO } from '@Models/model.types.ts';
import {
    ActionsCell,
    Button,
    Cell,
    EmptyState,
    EmptyStateActions,
    EmptyStateContent,
    EmptyStateDescription,
    EmptyStateIllustration,
    EmptyStateTitle,
    Row,
    SkeletonCheckbox,
    SkeletonText,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@tlx/atlas';
import React, { MutableRefObject, ReactNode, useEffect, useRef } from 'react';

import { formatNumber } from '@tlx/intl';

export const DivisionDisplay = ({ division }: { division?: DivisionDTO }) => {
    return (
        <Tooltip placement={'bottom-start'}>
            <TooltipTrigger>
                <span className={'atl-truncate'}>{division?.name}</span>
            </TooltipTrigger>
            <TooltipContent>{`(${division?.organizationNumber}) ${division?.name}`}</TooltipContent>
        </Tooltip>
    );
};

export function TextCellDash({ children }: { children: ReactNode }) {
    const content = children || '-';
    return <TextCell truncate={typeof content === 'string'}>{content}</TextCell>;
}

export function TextCellDashMoneyFormat({ children }: { children: number }) {
    return (
        <TextCell truncate>
            {children !== 0
                ? formatNumber(children, undefined, {
                      minimumFractionDigits: 2,
                  })
                : '-'}
        </TextCell>
    );
}

export const SkeletonRows = ({
    numberOfColumns,
    numberOfActions,
    checkbox,
}: {
    numberOfColumns: number;
    numberOfActions: number;
    checkbox?: boolean;
}) =>
    Array.from({ length: 10 }).map((_, index) => (
        <Row key={index}>
            {checkbox && (
                <Cell className="atl-p-8 atl-pl-12">
                    <SkeletonCheckbox />
                </Cell>
            )}
            {Array.from({ length: numberOfColumns }).map((_, index) => (
                <Cell key={index} className="atl-p-8">
                    <SkeletonText className={'atl-w-full'} />
                </Cell>
            ))}
            {numberOfActions > 0 && (
                <ActionsCell>
                    <div className={'atl-flex atl-gap-24'}>
                        {Array.from({ length: numberOfActions }).map((_, index) => (
                            <SkeletonCheckbox key={index} />
                        ))}
                    </div>
                </ActionsCell>
            )}
        </Row>
    ));

interface LoadMoreTargetProps {
    scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

export function LoadMoreTargetRow(props: LoadMoreTargetProps) {
    return (
        <Row>
            <Cell column={'1/-1'}>
                <LoadMoreTarget {...props} />
            </Cell>
        </Row>
    );
}

export function LoadMoreTarget({ scrollContainerRef, hasMore, onLoadMore }: LoadMoreTargetProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!hasMore) {
            return;
        }

        const target = ref.current;

        if (target === null) {
            return;
        }

        function onIntersection(entries: IntersectionObserverEntry[]) {
            // There can be multiple entries
            const isAnyEntriesIntersecting = entries.some((entry) => entry.isIntersecting);
            if (onLoadMore && isAnyEntriesIntersecting) {
                onLoadMore();
            }
        }

        const observer = new IntersectionObserver(onIntersection, {
            root: scrollContainerRef.current,
            rootMargin: '500px 0px',
        });

        observer.observe(target);

        return () => {
            observer.unobserve(target);
        };
    }, [hasMore, onLoadMore]);

    return (
        <div
            ref={ref}
            className={hasMore ? 'table-column-1 atl-justify-center atl-min-w-full atl-absolute' : 'atl-hidden'}
            style={{ height: '20px', marginTop: '-200px' }}
        />
    );
}

interface EmptyStateWrapperProps {
    titleMessage: React.ReactNode;
    descriptionMessage: React.ReactNode;
    buttonContent: React.ReactNode;
    onClickAction: () => void;
    illustration: React.ReactNode;
}

export const TableEmptyState: React.FC<EmptyStateWrapperProps> = ({
    titleMessage,
    descriptionMessage,
    buttonContent,
    onClickAction,
    illustration,
}) => {
    return (
        <Cell column={'1/-1'} className={'atl-bg-white'}>
            <EmptyState className="table-column-1 atl-justify-center atl-min-w-full">
                <EmptyStateIllustration>{illustration}</EmptyStateIllustration>
                <EmptyStateContent>
                    <EmptyStateTitle>{titleMessage}</EmptyStateTitle>
                    <EmptyStateDescription>{descriptionMessage}</EmptyStateDescription>
                    <EmptyStateActions>
                        <Button variant="secondary" data-testid="reset-filter" onClick={onClickAction}>
                            {buttonContent}
                        </Button>
                    </EmptyStateActions>
                </EmptyStateContent>
            </EmptyState>
        </Cell>
    );
};

//Once atlas is upgraded, we can swap this to just use numberCell but with formatOptions prop
export function TlxNumberCell({ children }: { children: number }) {
    return <Cell align="right">{formatNumber(children, undefined, { minimumFractionDigits: 2 })}</Cell>;
}
